import {
  BookOpen,
  Calendar,
  Clipboard,
  Home,
  MapPin,
  Medal,
  Ticket,
  Trophy,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  PERMISSION_LIST_KEY,
  LEARNER_DASHBOARD_PERMISSION,
  LEARNER_COURSES_PERMISSION,
  LEARNER_LEARNING_PATHS_PERMISSION,
  LEARNER_CERTIFICATIONS_PERMISSION,
  LEARNER_SCHEDULE_PERMISSION,
  LEARNER_TICKETS_PERMISSION,
  LEARNER_LEADERBOARD_PERMISSION,
  LEARNER_GAMIFICATION_PERMISSION,
  LEARNER_CONTACT_SUPPORT_PERMISSION,
} from '@/constants/constant-permissions';
import {
  LEARNER_DASHBOARD,
  LEARNER_COURSES,
  LEARNER_LEARNING_PATHS,
  LEARNER_CERTIFICATIONS,
  LEARNER_SCHEDULE,
  LEARNER_TICKETS,
  LEARNER_LEADERBOARD,
  LEARNER_MY_ACHIEVEMENTS,
  LEARNER_HELP,
} from '@/constants/constant-routes';

// Learner sidebar permission keys in display order (used by AssignPermissionToRole)
export const learnerSidebarPermissionKeys: string[][] = [
  [LEARNER_DASHBOARD_PERMISSION.LEARNER_DASHBOARD_READ],
  [LEARNER_COURSES_PERMISSION.LEARNER_COURSES_READ, LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE],
  [LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_READ, LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_WRITE],
  [LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_READ, LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_WRITE],
  [LEARNER_SCHEDULE_PERMISSION.LEARNER_SCHEDULE_READ],
  [LEARNER_TICKETS_PERMISSION.LEARNER_TICKETS_READ, LEARNER_TICKETS_PERMISSION.LEARNER_TICKETS_WRITE],
  [LEARNER_LEADERBOARD_PERMISSION.LEARNER_LEADERBOARD_READ],
  [LEARNER_GAMIFICATION_PERMISSION.LEARNER_GAMIFICATION_READ, LEARNER_GAMIFICATION_PERMISSION.LEARNER_GAMIFICATION_WRITE],
  [LEARNER_CONTACT_SUPPORT_PERMISSION.LEARNER_CONTACT_SUPPORT_READ, LEARNER_CONTACT_SUPPORT_PERMISSION.LEARNER_CONTACT_SUPPORT_WRITE],
];

interface LearnerSidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  onClose?: () => void;
  gradientEnabled?: boolean;
}

export default function LearnerSidebar({ isOpen, isCollapsed, onToggle, onClose, gradientEnabled = true }: LearnerSidebarProps) {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const navigate = useNavigate()
  
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1280);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Read user permissions once
  const userPermissions: string[] = useMemo(() =>
    JSON.parse(sessionStorage.getItem(PERMISSION_LIST_KEY) || '[]'),
  []);

  const canViewHelpSupport = userPermissions.includes(LEARNER_CONTACT_SUPPORT_PERMISSION.LEARNER_CONTACT_SUPPORT_READ);

  // Filter menu items based on user permissions
  const menuItems = useMemo(() => {
    const hasAnyPermission = (permissionKeys?: string[]) => {
      if (!permissionKeys || permissionKeys.length === 0) return true;
      return permissionKeys.some((key) => userPermissions.includes(key));
    };

    const allMenuItems = [
      {
        section: 'MAIN',
        items: [
          {
            path: LEARNER_DASHBOARD,
            label: 'Dashboard',
            icon: Home,
            iconColor: 'text-blue-600',
            badge: null,
            permissionKeys: [LEARNER_DASHBOARD_PERMISSION.LEARNER_DASHBOARD_READ],
          },
          {
            path: LEARNER_COURSES,
            label: 'My Courses',
            icon: BookOpen,
            iconColor: 'text-purple-600',
            badge: null,
            permissionKeys: [LEARNER_COURSES_PERMISSION.LEARNER_COURSES_READ, LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE],
          },
          {
            path: LEARNER_LEARNING_PATHS,
            label: 'Learning Paths',
            icon: MapPin,
            iconColor: 'text-green-600',
            badge: null,
            permissionKeys: [LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_READ, LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_WRITE],
          },
          {
            path: LEARNER_CERTIFICATIONS,
            label: 'My Certifications',
            icon: Clipboard,
            iconColor: 'text-amber-600',
            badge: null,
            permissionKeys: [LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_READ, LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_WRITE],
          },
        ],
      },
      {
        section: 'ACTIVITY',
        items: [
          {
            path: LEARNER_SCHEDULE,
            label: 'Schedule',
            icon: Calendar,
            iconColor: 'text-indigo-600',
            badge: null,
            permissionKeys: [LEARNER_SCHEDULE_PERMISSION.LEARNER_SCHEDULE_READ],
          },
          {
            path: LEARNER_TICKETS,
            label: 'Tickets',
            icon: Ticket,
            iconColor: 'text-pink-600',
            badge: null,
            permissionKeys: [LEARNER_TICKETS_PERMISSION.LEARNER_TICKETS_READ, LEARNER_TICKETS_PERMISSION.LEARNER_TICKETS_WRITE],
          },
        ],
      },
      {
        section: 'Achievements',
        items: [
          {
            path: LEARNER_LEADERBOARD,
            label: 'LeaderBoard',
            icon: Trophy,
            iconColor: 'text-yellow-600',
            badge: null,
            permissionKeys: [LEARNER_LEADERBOARD_PERMISSION.LEARNER_LEADERBOARD_READ],
          },
          {
            path: LEARNER_MY_ACHIEVEMENTS,
            label: 'My Achievements',
            icon: Medal,
            iconColor: 'text-red-600',
            badge: null,
            permissionKeys: [LEARNER_GAMIFICATION_PERMISSION.LEARNER_GAMIFICATION_READ, LEARNER_GAMIFICATION_PERMISSION.LEARNER_GAMIFICATION_WRITE],
          },
        ],
      },
    ];

    // Filter items by permissions and remove empty sections
    return allMenuItems
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => hasAnyPermission(item.permissionKeys)),
      }))
      .filter((group) => group.items.length > 0);
  }, [userPermissions]);

  return (
    <nav className="flex-1 overflow-y-auto py-4">
      {/* Learning Stats Card - Commented out until server-side API is implemented */}
      {/*
      {!isCollapsed && (
        <div className={`mx-4 mb-4 p-3 rounded-lg border ${
          gradientEnabled
            ? 'bg-gradient-to-br from-blue-500/10 via-indigo-500/10 to-purple-500/10 border-primary-200/30 backdrop-blur-sm'
            : 'bg-primary-50 border-primary-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary-600" />
              <span className="text-xs font-semibold text-primary-700">Today's Learning</span>
            </div>
            <span className="text-xs text-primary-600">2h 15m</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-primary-600" />
              <span className="text-xs font-semibold text-primary-700">Weekly Streak</span>
            </div>
            <span className="text-xs text-primary-600">5 days</span>
          </div>
        </div>
      )}
      */}

      {/* Menu Items */}
      {menuItems.map((group) => (
        <div key={group.section} className="mb-6">
          <h3 className={`px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 ${isCollapsed ? 'xl:hidden' : ''}`}>
            {group.section}
          </h3>
          {group.items.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
                           location.pathname.startsWith(item.path + '/');
            
            const handleMouseEnter = () => {
              // Only apply hover behavior on desktop when sidebar is collapsed
              if (isCollapsed && isDesktop) {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                  hoverTimeoutRef.current = null;
                }
                setHoveredItem(item.label);
              }
            };

            const handleMouseLeave = (e: React.MouseEvent) => {
              // Only apply hover behavior on desktop when sidebar is collapsed
              if (isCollapsed && isDesktop) {
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (!relatedTarget?.closest('.dropdown-menu') && !relatedTarget?.closest('.menu-item')) {
                  hoverTimeoutRef.current = setTimeout(() => {
                    setHoveredItem(null);
                  }, 100);
                }
              }
            };
            
            return (
              <div key={item.path} className="relative menu-item"
                   onMouseEnter={handleMouseEnter}
                   onMouseLeave={handleMouseLeave}>
                <NavLink
                  to={item.path}
                  onClick={() => {
                    // Close mobile sidebar when item is clicked
                    if (!isDesktop && onClose) {
                      onClose();
                    }
                  }}
                  className={`block px-4 py-2 mx-2 rounded-lg transition-all group ${
                    isActive
                      ? gradientEnabled
                        ? 'bg-gradient-to-r from-primary-100 to-indigo-100/50 text-primary-700 shadow-sm'
                        : 'bg-primary-100 text-primary-700'
                      : gradientEnabled
                        ? 'text-gray-600 hover:bg-gradient-to-r hover:from-white/50 hover:to-gray-100/30 hover:text-gray-900'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  title={isCollapsed && isDesktop ? item.label : undefined}
                >
                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={isActive ? 'opacity-100' : 'opacity-80 group-hover:opacity-100 transition-opacity'}>
                      <Icon className={`w-5 h-5 ${isActive ? 'text-primary-600' : item.iconColor || 'text-gray-500'}`} />
                    </span>
                    <span className={`text-sm font-medium ${isCollapsed ? 'xl:hidden' : ''}`}>{item.label}</span>
                  </div>
                  {item.badge && !isCollapsed && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      item.badge === 'New' 
                        ? 'bg-green-100 text-green-700'
                        : isActive
                        ? 'bg-primary-200 text-primary-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                  </div>
                </NavLink>
                
                {/* Flyout tooltip for collapsed sidebar - only on desktop */}
                {isCollapsed && hoveredItem === item.label && isDesktop && (
                  <>
                    {/* Invisible bridge to maintain hover */}
                    <div className="absolute left-full top-0 w-4 h-full" />
                    <div className="dropdown-menu absolute left-[calc(100%+4px)] top-1/2 -translate-y-1/2 z-[9999]" 
                         onMouseEnter={() => {
                           if (hoverTimeoutRef.current) {
                             clearTimeout(hoverTimeoutRef.current);
                             hoverTimeoutRef.current = null;
                           }
                           setHoveredItem(item.label);
                         }}
                         onMouseLeave={() => {
                           hoverTimeoutRef.current = setTimeout(() => setHoveredItem(null), 100);
                         }}>
                      <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap flex items-center gap-2">
                        <span className="text-sm font-medium">{item.label}</span>
                        {item.badge && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            item.badge === 'New' 
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-700 text-gray-200'
                          }`}>
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Help Card — hidden only in the collapsed desktop sidebar; on phones the
          sidebar is always full width, whatever was remembered from desktop. */}
      {canViewHelpSupport && (
        <div className={`mx-4 mt-8 p-4 rounded-lg border ${isCollapsed ? 'xl:hidden' : ''} ${
          gradientEnabled
            ? 'bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-indigo-500/10 border-purple-200/30 backdrop-blur-sm shadow-sm'
            : 'bg-purple-50 border-purple-200'
        }`}>
          <h4 className="text-sm font-semibold text-purple-900 mb-2">
            Need Help?
          </h4>
          <p className="text-xs text-purple-700 mb-3">
            Our support team is here to help you succeed in your learning journey.
          </p>
          <button className={`w-full px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-all ${
          gradientEnabled
            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-sm'
            : 'bg-purple-600 hover:bg-purple-700'
        }`} onClick={() => navigate(LEARNER_HELP)}>
            Contact Support
          </button>
        </div>
      )}
    </nav>
  );
}